import React, { useCallback, useRef, useState } from 'react';
import {
    AlertCircle,
    Cast,
    ChevronDown,
    Maximize2,
    Pause,
    Play,
    Power,
    RotateCcw,
    RotateCw,
    Volume2,
    VolumeX,
    X
} from 'lucide-react';
import { useCast } from '../context/CastContext';
import './CastMiniPlayer.css';

const SKIP_SECONDS = 10;
const SEEK_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];

const formatTime = (seconds) => {
    if (!seconds || !Number.isFinite(seconds)) return '0:00';
    const total = Math.floor(Math.abs(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const remainder = total % 60;
    return hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${remainder.toString().padStart(2, '0')}`
        : `${minutes}:${remainder.toString().padStart(2, '0')}`;
};

const formatEndTime = (remainingSeconds) => {
    if (!(remainingSeconds > 0) || !Number.isFinite(remainingSeconds)) return '';
    const end = new Date(Date.now() + remainingSeconds * 1000);
    return `Fin à ${end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

const CastMiniPlayer = ({ onOpen }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [scrubTime, setScrubTime] = useState(null);
    const scrubTimeRef = useRef(null);
    const {
        isConnected,
        isConnecting,
        isLoading,
        deviceName,
        error,
        clearError,
        media,
        castContent,
        currentTime,
        duration,
        isPaused,
        isSeeking,
        isEnded,
        volume,
        isMuted,
        playOrPause,
        replay,
        seekTo,
        setRemoteVolume,
        toggleRemoteMute,
        stopCasting
    } = useCast();

    const canOpen = Boolean(onOpen && castContent?.content && castContent.contentId === media.contentId);
    const openPlayer = useCallback(() => {
        if (canOpen) onOpen(castContent.content);
    }, [canOpen, castContent, onOpen]);

    if (!isConnected || !media.title) return null;

    const canSeek = !media.isLive && duration > 0;
    const displayedTime = scrubTime ?? currentTime;
    const seekMaximum = Math.max(1, duration || 0);
    const volumePercent = Math.round(Math.max(0, Math.min(1, Number(volume) || 0)) * 100);
    const seekPercent = duration > 0
        ? Math.max(0, Math.min(100, (displayedTime / duration) * 100))
        : 0;
    const remaining = Math.max(0, duration - displayedTime);

    const statusLabel = isConnecting
        ? `Connexion à ${deviceName || 'Chromecast'}`
        : isEnded
            ? `Terminé sur ${deviceName || 'Chromecast'}`
            : `Sur ${deviceName || 'Chromecast'}`;
    const metaLabel = media.isLive
        ? 'En direct'
        : isSeeking
            ? `Reprise à ${formatTime(displayedTime)}…`
            : duration > 0
                ? `${formatTime(displayedTime)} / ${formatTime(duration)}`
                : media.subtitle || 'LUMA';

    const handleSeekChange = (event) => {
        if (!canSeek) return;
        const target = Math.max(0, Math.min(duration, Number(event.currentTarget.value) || 0));
        scrubTimeRef.current = target;
        setScrubTime(target);
    };

    const commitSeek = (value, options) => {
        if (!canSeek || scrubTimeRef.current === null) return;
        const target = Math.max(0, Math.min(duration, Number(value) || 0));
        scrubTimeRef.current = null;
        setScrubTime(null);
        seekTo(target, options);
    };

    const handleSeekKeyUp = (event) => {
        // Arrow presses are merged by the provider into one TV seek.
        if (SEEK_KEYS.includes(event.key)) commitSeek(event.currentTarget.value);
    };

    const handlePrimaryAction = () => {
        if (isEnded) {
            replay().catch((replayError) => console.error('[Cast] Relecture impossible:', replayError));
            return;
        }
        playOrPause();
    };

    const primaryLabel = isEnded
        ? 'Revoir depuis le début'
        : isPaused ? 'Lire sur le téléviseur' : 'Mettre en pause sur le téléviseur';

    return (
        <section className={`cast-mini-player ${isExpanded ? 'expanded' : ''}`} aria-label="Contrôle Chromecast">
            <div className="cmp-main-row">
                <button
                    type="button"
                    className="cmp-media"
                    onClick={openPlayer}
                    disabled={!canOpen}
                    aria-label={canOpen ? `Ouvrir ${media.title} en plein écran` : media.title}
                >
                    {media.imageUrl ? (
                        <img src={media.imageUrl} alt="" className="cmp-thumb" />
                    ) : (
                        <span className="cmp-thumb cmp-thumb-placeholder"><Cast size={17} /></span>
                    )}
                    <span className="cmp-info">
                        <span className="cmp-device">
                            <span className={`cmp-status-dot ${isLoading ? 'loading' : ''} ${isEnded ? 'ended' : ''}`} />
                            <span className="cmp-device-label">{statusLabel}</span>
                        </span>
                        <span className="cmp-title">{media.title}</span>
                        <span className="cmp-meta">
                            {media.isLive && <span className="cmp-live-chip">Live</span>}
                            {metaLabel}
                        </span>
                    </span>
                </button>

                <div className="cmp-primary-controls">
                    <button
                        type="button"
                        className="cmp-icon-btn"
                        onClick={() => seekTo(currentTime - SKIP_SECONDS)}
                        disabled={!canSeek || isEnded}
                        aria-label="Reculer de 10 secondes"
                    >
                        <RotateCcw size={17} />
                    </button>
                    <button
                        type="button"
                        className="cmp-icon-btn cmp-play-btn"
                        onClick={handlePrimaryAction}
                        aria-label={primaryLabel}
                        title={primaryLabel}
                    >
                        {isLoading && !isEnded
                            ? <span className="cmp-loading-ring" />
                            : isEnded
                                ? <RotateCcw size={18} />
                                : isPaused ? <Play size={19} fill="currentColor" /> : <Pause size={19} fill="currentColor" />}
                    </button>
                    <button
                        type="button"
                        className="cmp-icon-btn"
                        onClick={() => seekTo(currentTime + SKIP_SECONDS)}
                        disabled={!canSeek || isEnded}
                        aria-label="Avancer de 10 secondes"
                    >
                        <RotateCw size={17} />
                    </button>
                    <button
                        type="button"
                        className="cmp-icon-btn cmp-expand-btn"
                        onClick={() => setIsExpanded((value) => !value)}
                        aria-expanded={isExpanded}
                        aria-controls="cast-mini-player-details"
                        aria-label={isExpanded ? 'Réduire les contrôles' : 'Afficher tous les contrôles'}
                    >
                        <ChevronDown size={17} />
                    </button>
                </div>
            </div>

            <div className="cmp-timeline">
                <input
                    className="cmp-seek-slider"
                    type="range"
                    min="0"
                    max={seekMaximum}
                    step="1"
                    value={Math.max(0, Math.min(seekMaximum, displayedTime || 0))}
                    style={{ '--range-progress': `${media.isLive ? 100 : seekPercent}%` }}
                    onChange={handleSeekChange}
                    onPointerUp={(event) => commitSeek(event.currentTarget.value, { immediate: true })}
                    onPointerCancel={() => {
                        scrubTimeRef.current = null;
                        setScrubTime(null);
                    }}
                    onKeyUp={handleSeekKeyUp}
                    onBlur={(event) => commitSeek(event.currentTarget.value, { immediate: true })}
                    disabled={!canSeek}
                    aria-label="Position de lecture sur le téléviseur"
                    aria-valuetext={canSeek ? `${formatTime(displayedTime)} sur ${formatTime(duration)}` : 'En direct'}
                />
            </div>

            {error && (
                <div className="cmp-error" role="alert">
                    <AlertCircle size={14} />
                    <span>{error}</span>
                    <button type="button" className="cmp-error-close" onClick={clearError} aria-label="Masquer l’erreur">
                        <X size={13} />
                    </button>
                </div>
            )}

            {isExpanded && (
                <div className="cmp-details" id="cast-mini-player-details">
                    <div className="cmp-volume-control">
                        <button
                            type="button"
                            className="cmp-icon-btn cmp-volume-btn"
                            onClick={toggleRemoteMute}
                            aria-label={isMuted ? 'Réactiver le son du téléviseur' : 'Couper le son du téléviseur'}
                        >
                            {isMuted ? <VolumeX size={17} /> : <Volume2 size={17} />}
                        </button>
                        <input
                            className="cmp-volume-slider"
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={volumePercent}
                            style={{ '--range-progress': `${volumePercent}%` }}
                            onPointerDown={() => {
                                if (isMuted) toggleRemoteMute();
                            }}
                            onKeyDown={(event) => {
                                if (isMuted && SEEK_KEYS.includes(event.key)) toggleRemoteMute();
                            }}
                            onChange={(event) => setRemoteVolume(Number(event.currentTarget.value) / 100)}
                            aria-label="Volume du téléviseur"
                        />
                        <span className="cmp-volume-value">{isMuted ? 'Muet' : `${volumePercent}%`}</span>
                    </div>

                    {canSeek && !isEnded && <span className="cmp-end-time">{formatEndTime(remaining)}</span>}

                    {canOpen && (
                        <button type="button" className="cmp-icon-btn" onClick={openPlayer} aria-label="Ouvrir le lecteur plein écran" title="Plein écran">
                            <Maximize2 size={16} />
                        </button>
                    )}

                    <button type="button" className="cmp-stop-btn" onClick={stopCasting}>
                        <Power size={15} />
                        <span>Arrêter la diffusion</span>
                    </button>
                </div>
            )}
        </section>
    );
};

export default CastMiniPlayer;
